import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from 'react'
import { cn } from '@/lib/cn'

type FieldProps = {
  label: ReactNode
  hint?: ReactNode
  error?: string | null
  required?: boolean
  className?: string
  /** A single form control; receives id / aria-* wiring. */
  children: ReactElement<Record<string, unknown>>
  aside?: ReactNode
}

export function Field({ label, hint, error, required, className, children, aside }: FieldProps) {
  const id = useId()
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined

  const control = isValidElement(children)
    ? cloneElement(children, {
        id: (children.props.id as string) ?? id,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': describedBy,
        'aria-required': required || undefined,
      })
    : children

  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      <div className="flex items-center justify-between gap-2">
        <label htmlFor={(children.props.id as string) ?? id} className="text-[length:calc(12.5px*var(--text-scale))] font-medium text-fg-2">
          {label}
          {required && (
            <span className="ml-0.5 text-danger" aria-hidden>
              *
            </span>
          )}
        </label>
        {aside}
      </div>
      {control}
      {error ? (
        <p id={`${id}-error`} className="text-[length:calc(12.5px*var(--text-scale))] text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-[length:calc(12.5px*var(--text-scale))] text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  )
}
